import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  CameraAltOutlined as CameraAltOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  HubOutlined as HubOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  StorefrontOutlined as StorefrontOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  LocalGasStationOutlined as LocalGasStationOutlinedIcon,
} from "@mui/icons-material";
import {
  Calendar as CalIcon,
  Bell,
  CreditCard,
  FileText,
  Globe,
  LifeBuoy,
  Link as LinkIcon,
  Megaphone,
  Settings as SettingsIcon,
} from "lucide-react";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import { isDemoReadOnlyUser } from "../../utils/demoUser";
import {
  getManageableRoles,
  isExplicitSuperAdminRole,
  normalizeRole,
  ROLES,
} from "../../utils/rbacHelper";
import {
  buildAdminDashboardPath,
  isDashboardShellPath,
  readDashboardTabFromSearch,
  resolveDashboardTabFromPath,
} from "../../router/dashboardNavigation";

const REPORT_MENU_ITEMS = [
  "Overview",
  "Engine Report",
  "Device Logs Report",
  "Ignition Report",
  "ACC / Ignition Summary",
  "Daily Summary Report",
  "Trip History Report",
  "Trip Report",
  "Movement Report",
  "Distance Report",
  "Stoppage Report",
  "Overspeed Report",
  "Idle Report",
  "Fuel Report",
  "Geofence Report",
  "Temperature Report",
  "Travel Path Report",
  "Working Hours Report",
  "RFID Report",
  "View All Reports",
];

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks", path: "/tasks/all" },
  { key: "create", label: "Create Task", path: "/tasks/create" },
  { key: "inventory", label: "Inventory", path: "/tasks/inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const BILLING_MENU_ITEMS = [
  { key: "licenses", label: "Licenses", path: "/licenses" },
  { key: "all-licenses", label: "All Licenses", path: "/licenses?section=all-licenses" },
  { key: "purchase-license", label: "Purchase License", path: "/licenses?section=purchase-license" },
  { key: "license-summary", label: "License Summary", path: "/licenses?section=license-summary" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const HISTORY_TABS = ["history_playback", "history_logs", "history_events", "history_parking"];
const SETTINGS_MENU_ITEMS = [
  { key: "general", label: "General Settings", icon: <SettingsIcon size={16} /> },
  { key: "links", label: "Quick Links", icon: <LinkIcon size={16} /> },
  { key: "language", label: "Language Settings", icon: <Globe size={16} /> },
  { key: "notifications", label: "Notification Settings", icon: <Bell size={16} /> },
  { key: "announcements", label: "Announcements", icon: <Megaphone size={16} /> },
  { key: "support", label: "Support Tickets", icon: <LifeBuoy size={16} /> },
  { key: "plans", label: "Recharge Plans", icon: <CreditCard size={16} /> },
  { key: "calendar", label: "Calendar", icon: <CalIcon size={16} /> },
  { key: "renewalRequest", label: "Renewal Requests", icon: <FileText size={16} /> },
  { key: "reports", label: "Report Scheduler", icon: <FileText size={16} /> },
];

export default function useGlobalSidebarItems() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);
  const isDemoReadOnly = isDemoReadOnlyUser(currentUser);

  return useMemo(() => {
    const navigateFromSidebar = (targetPath) => {
      const nextPath = String(targetPath || "").trim();
      if (!nextPath) return;
      const currentHref = `${location.pathname}${location.search}${location.hash}`;
      const targetUrl = new URL(nextPath, window.location.origin);
      const targetHref = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
      const isCurrentDashboardFamily = isDashboardShellPath(location.pathname);
      const isTargetDashboardFamily = isDashboardShellPath(nextPath);
      const isVehicleCompatibilityPage = location.pathname === "/vehicles";

      if (targetUrl.pathname === location.pathname && targetHref !== currentHref) {
        window.location.assign(targetHref);
        return;
      }

      if (
        (isCurrentDashboardFamily && !isTargetDashboardFamily) ||
        (isVehicleCompatibilityPage && nextPath !== "/vehicles")
      ) {
        window.location.assign(nextPath);
        return;
      }

      navigate(nextPath);
    };

    const searchParams = new URLSearchParams(location.search);
    const isAdminDashboardRoute = isDashboardShellPath(location.pathname);
    const dashboardTab = isAdminDashboardRoute
      ? resolveDashboardTabFromPath(location.pathname, readDashboardTabFromSearch(location.search, ""))
      : readDashboardTabFromSearch(location.search, "");
    const messageSection = searchParams.get("section") || "compose";
    const reportSection = searchParams.get("report") || "Overview";
    const billingSection = searchParams.get("section") || "licenses";
    const roleRaw = currentUser?.role || "";
    const role = normalizeRole(roleRaw);
    const isSuperAdmin = role === ROLES.SUPER_ADMIN;
    const canAccessWhiteLabel =
      isExplicitSuperAdminRole(roleRaw) && !isDemoReadOnly;

    const hiddenDemoUserRoleKeys = new Set([
      "sub-admin",
      "manager",
      "hr",
      "admin",
      "office-employee",
      "field-employee",
    ]);
    const superAdminOnlyRoleKeys = new Set([
      "manager",
      "hr",
      "admin",
      "office-employee",
      "field-employee",
    ]);

    const manageableRoleSet = new Set(
      (isSuperAdmin
        ? USER_ROLE_ROUTE_CONFIG.map((item) => normalizeRole(item.role))
        : getManageableRoles(role).map((item) => normalizeRole(item)))
    );

    const visibleUserRoleRoutes = USER_ROLE_ROUTE_CONFIG.filter((item) => {
      const normalizedItemRole = normalizeRole(item.role);
      return (
        manageableRoleSet.has(normalizedItemRole) &&
        !(isDemoReadOnly && hiddenDemoUserRoleKeys.has(item.key)) &&
        (!superAdminOnlyRoleKeys.has(item.key) || isSuperAdmin)
      );
    });
    const hidePurchaseLicenseForRole =
      isDemoReadOnly || role === ROLES.DEALER;
    const visibleBillingMenuItems = BILLING_MENU_ITEMS.filter(
      (item) => !(hidePurchaseLicenseForRole && item.key === "purchase-license")
    );
    const canSeeAllSettings = ["admin", "manager", "sub_admin", "group_admin"].includes(role);
    const hideSettingsForUser = role === "user";
    const hideSettingsForViewer = role === "viewer";
    const isFieldEmployee = role === "field_emp";
    const canSeeRenewalRequest = ["admin", "manager", "sub_admin", "group_admin"].includes(role);
    const visibleSettingsMenuItems = SETTINGS_MENU_ITEMS.filter((item) => {
      if (isFieldEmployee) return item.key === "general";
      if (!canSeeRenewalRequest && item.key === "renewalRequest") return false;
      if (canSeeAllSettings) return true;
      if (hideSettingsForViewer) return !["plans", "reports", "renewalRequest"].includes(item.key);
      if (hideSettingsForUser) return !["plans", "renewalRequest"].includes(item.key);
      return true;
    });

    return [
      {
        key: "dashboard",
        label: "Dashboard",
        icon: <AssessmentOutlinedIcon fontSize="small" />,
        active:
          (isAdminDashboardRoute && dashboardTab === "statics") ||
          (location.pathname === "/dashboard" && !searchParams.get("tab")),
        onClick: () =>
          navigateFromSidebar(buildAdminDashboardPath({ tab: "statics" })),
      },
      {
        key: "vehicles",
        label: "Vehicles",
        icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
        active: isAdminDashboardRoute && dashboardTab === "devices",
        onClick: () =>
          navigateFromSidebar(buildAdminDashboardPath({ tab: "devices" })),
      },
      {
        key: "map",
        label: "Map",
        icon: <PlaceOutlinedIcon fontSize="small" />,
        active: isAdminDashboardRoute && dashboardTab === "map",
        defaultExpanded: isAdminDashboardRoute && dashboardTab === "map",
        children: [
          {
            key: "map-live-tracking",
            label: "Live Tracking",
            active: isAdminDashboardRoute && dashboardTab === "map",
            onClick: () =>
              navigateFromSidebar(buildAdminDashboardPath({ tab: "map" })),
          },
        ],
      },
      {
        key: "history",
        label: "History",
        icon: <HistoryOutlinedIcon fontSize="small" />,
        active: isAdminDashboardRoute && HISTORY_TABS.includes(dashboardTab),
        defaultExpanded: isAdminDashboardRoute && HISTORY_TABS.includes(dashboardTab),
        children: [
          {
            key: "history-playback",
            label: "Playback",
            active: dashboardTab === "history_playback",
            onClick: () =>
              navigateFromSidebar(
                buildAdminDashboardPath({ tab: "history_playback" })
              ),
          },
          {
            key: "history-logs",
            label: "Logs",
            active: dashboardTab === "history_logs",
            onClick: () =>
              navigateFromSidebar(
                buildAdminDashboardPath({ tab: "history_logs" })
              ),
          },
          {
            key: "history-events",
            label: "Events",
            active: dashboardTab === "history_events",
            onClick: () =>
              navigateFromSidebar(
                buildAdminDashboardPath({ tab: "history_events" })
              ),
          },
          {
            key: "history-parking",
            label: "Parking",
            active: dashboardTab === "history_parking",
            onClick: () =>
              navigateFromSidebar(
                buildAdminDashboardPath({ tab: "history_parking" })
              ),
          },
        ],
      },
      {
        key: "fuel-monitoring",
        label: "Fuel Monitoring",
        icon: <LocalGasStationOutlinedIcon fontSize="small" />,
        active: location.pathname === "/fuel-monitoring",
        onClick: () => navigateFromSidebar("/fuel-monitoring"),
      },
      {
        key: "geofences",
        label: "Geofences",
        icon: <PlaceOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/geofences"),
        defaultExpanded: location.pathname.startsWith("/geofences"),
        children: [
          {
            key: "geofences-create",
            label: "Create Geofence",
            active: location.pathname === "/geofences/create",
            onClick: () => navigateFromSidebar("/geofences/create"),
          },
          {
            key: "geofences-list",
            label: "Geofence List",
            active: location.pathname === "/geofences/list" || location.pathname === "/geofences",
            onClick: () => navigateFromSidebar("/geofences/list"),
          },
        ],
      },
      ...(canAccessWhiteLabel
        ? [
            {
              key: "white-label",
              label: "White Label",
              icon: <StorefrontOutlinedIcon fontSize="small" />,
              active: isAdminDashboardRoute && dashboardTab === "white_label",
              onClick: () =>
                navigateFromSidebar(
                  buildAdminDashboardPath({ tab: "white_label" })
                ),
            },
          ]
        : []),
      {
        key: "reports",
        label: "Reports",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        active: location.pathname === "/reports",
        defaultExpanded: location.pathname === "/reports",
        onClick: () => navigateFromSidebar("/reports?report=Overview"),
        children: REPORT_MENU_ITEMS.map((item) => ({
          key: `report-${item}`,
          label: item,
          active: location.pathname === "/reports" && reportSection === item,
          onClick: () => navigateFromSidebar(`/reports?report=${encodeURIComponent(item)}`),
        })),
      },
      ...(!isDemoReadOnly && isSuperAdmin
        ? [
            {
              key: "tasks",
              label: "Tasks",
              icon: <TaskAltOutlinedIcon fontSize="small" />,
              active: location.pathname.startsWith("/tasks"),
              defaultExpanded: location.pathname.startsWith("/tasks"),
              children: TASK_MENU_ITEMS.map((item) => ({
                key: item.key,
                label: item.label,
                active: location.pathname === item.path,
                onClick: () => navigateFromSidebar(item.path),
              })),
            },
            {
              key: "messages",
              label: "Messages",
              icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
              active: location.pathname === "/message",
              defaultExpanded: location.pathname === "/message",
              children: MESSAGE_MENU_ITEMS.map((item) => ({
                key: item.key,
                label: item.label,
                active: location.pathname === "/message" && messageSection === item.key,
                onClick: () => navigateFromSidebar(`/message?section=${item.key}`),
              })),
            },
            {
              key: "protocols",
              label: "Protocol",
              icon: <HubOutlinedIcon fontSize="small" />,
              active: location.pathname.startsWith("/protocols"),
              onClick: () => navigateFromSidebar("/protocols"),
            },
            {
              key: "live-camera",
              label: "Live Camera",
              icon: <CameraAltOutlinedIcon fontSize="small" />,
              active: location.pathname.startsWith("/camera-portal"),
              onClick: () => navigateFromSidebar("/camera-portal"),
            },
          ]
        : []),
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        defaultExpanded: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigateFromSidebar("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigateFromSidebar("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigateFromSidebar("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigateFromSidebar("/logistic/documents") },
        ],
      },
      {
        key: "groups",
        label: "Groups",
        icon: <Groups2OutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/groups"),
        onClick: () => navigateFromSidebar("/groups"),
      },
      {
        key: "reminders",
        label: "Reminders",
        icon: <EventNoteOutlinedIcon fontSize="small" />,
        active: location.pathname === "/reminders",
        onClick: () => navigateFromSidebar("/reminders"),
      },
      {
        key: "routes",
        label: "Routes",
        icon: <RouteOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/routes"),
        defaultExpanded: location.pathname.startsWith("/routes"),
        children: [
          {
            key: "routes-create",
            label: "Create Route",
            active: location.pathname === "/routes/create",
            onClick: () => navigateFromSidebar("/routes/create"),
          },
          {
            key: "routes-list",
            label: "Route List",
            active: location.pathname === "/routes/list" || location.pathname === "/routes",
            onClick: () => navigateFromSidebar("/routes/list"),
          },
        ],
      },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        defaultExpanded: location.pathname.startsWith("/expense"),
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigateFromSidebar(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        defaultExpanded: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigateFromSidebar(item.path),
        })),
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        defaultExpanded: location.pathname.startsWith("/users"),
        children: visibleUserRoleRoutes.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigateFromSidebar(item.path),
        })),
      },
      role !== ROLES.USER
        ? {
            key: "billing",
            label: "Billing & Licenses",
            icon: <AssessmentOutlinedIcon fontSize="small" />,
            active: location.pathname === "/licenses",
            defaultExpanded: location.pathname === "/licenses",
            children: visibleBillingMenuItems.map((item) => ({
              key: item.key,
              label: item.label,
              active:
                location.pathname === "/licenses" &&
                ((item.key === "licenses" && !searchParams.get("section")) ||
                  searchParams.get("section") === item.key),
              onClick: () => navigateFromSidebar(item.path),
            })),
          }
        : null,
      {
        key: "settings",
        label: "Settings",
        icon: <SettingsOutlinedIcon fontSize="small" />,
        active: location.pathname === "/profile",
        defaultExpanded: location.pathname === "/profile",
        onClick: () => navigateFromSidebar("/profile?section=general"),
        children: visibleSettingsMenuItems.map((item) => ({
          key: `settings-${item.key}`,
          label: item.label,
          active:
            location.pathname === "/profile" &&
            (searchParams.get("section") || "general") === item.key,
          onClick: () => navigateFromSidebar(`/profile?section=${encodeURIComponent(item.key)}`),
        })),
      },
    ].filter(Boolean);
  }, [currentUser?.role, isDemoReadOnly, location.pathname, location.search, navigate]);
}
